import { type ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Card, Flex, Text, Grid, AgGridReact, AgGrid } from "@jsq/ds/core";
import { client as apolloClient } from "~/apollo/client";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import { PAGINATION } from "~/pages/Billing/constants";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import {
  AllClientEntitiesDataSource,
  type GroupedEntityRow,
} from "./allClientEntitiesDataSource";
import { getColumnDefinitions, autoGroupColumnDef } from "./columnDefinitions";
import { NoRowsOverlay } from "./NoRowsOverlay";
import {
  allClientEntitiesCard,
  allClientEntitiesHeader,
  allClientEntitiesHelperText,
  allClientEntitiesGridWrapper,
} from "./styles_vanilla.css";
import { getRowStyle, getRowId, isServerSideGroup } from "./utils";

const sideBarDef: AgGrid.SideBarDef = {
  position: "right",
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnFilter: false,
        suppressColumnSelectAll: false,
        suppressColumnExpandAll: true,
      },
    },
  ],
};

export const AllClientEntities = (): ReactElement => {
  const { asOfDateString } = useBillingDate();
  const [gridApi, setGridApi] = useState<AgGrid.GridApi<GroupedEntityRow>>();
  const [totalClientCount, setTotalClientCount] = useState<number | null>(null);

  const dataSource = useMemo(() => {
    return new AllClientEntitiesDataSource(
      { onTotalCountChange: setTotalClientCount },
      apolloClient,
      asOfDateString,
    );
  }, [asOfDateString]);

  useEffect(() => {
    if (dataSource && gridApi) {
      gridApi.setGridOption("serverSideDatasource", dataSource);
      gridApi.refreshServerSide({ purge: true });
    }
  }, [gridApi, dataSource]);

  const handleOnGridReady = useCallback((event: AgGrid.GridReadyEvent<GroupedEntityRow>) => {
    setGridApi(event.api);
  }, []);

  const colDefs = useMemo(() => getColumnDefinitions(), []);

  const gridOptions: AgGridReact.AgGridReactProps<GroupedEntityRow>["gridOptions"] = useMemo(
    () => ({
      suppressCellFocus: true,
      rowHeight: 48,
      domLayout: "normal",
      statusBar: { statusPanels: [{ statusPanel: "agTotalRowCountComponent" }] },
      rowModelType: "serverSide" as const,
      getRowId,
      pagination: true,
      paginationPageSizeSelector: false,
      cacheBlockSize: PAGINATION.ALL_CLIENT_ENTITIES_PAGE_SIZE,
      paginationPageSize: PAGINATION.ALL_CLIENT_ENTITIES_PAGE_SIZE,
      onGridReady: handleOnGridReady,
      defaultColDef: { flex: 1, menuTabs: ["generalMenuTab", "filterMenuTab"] },
      getRowStyle,
      sideBar: sideBarDef,
      groupDisplayType: "multipleColumns",
      autoGroupColumnDef,
      isServerSideGroup,
      noRowsOverlayComponent: NoRowsOverlay,
    }),
    [handleOnGridReady],
  );

  return (
    <Flex data-testid="all-client-entities-table" flexGrow={1}>
      <Card className={allClientEntitiesCard}>
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          className={allClientEntitiesHeader}
        >
          <Flex flexDirection="column">
            <Text variant="h3">All client entities</Text>
            <Text variant="secondary-sm" className={allClientEntitiesHelperText}>
              {totalClientCount !== null ? `${totalClientCount} clients` : "Loading..."}
            </Text>
          </Flex>
        </Flex>
        <div className={allClientEntitiesGridWrapper}>
          <Grid
            gridOptions={gridOptions}
            columnDefs={colDefs}
            loadingCellRenderer={LoadingCellRenderer}
          />
        </div>
      </Card>
    </Flex>
  );
};
